/**
 * Liste en temps réel des étudiants connectés au portail PWA.
 * Vue scolarité avec filtres + auto-refresh.
 */
import React, { useState, useMemo } from 'react';
import { useGetEtudiantsConnectesQuery, EtudiantConnecteItem } from '../../services/portailAdminApi';
import PhotoLightbox from '../../components/ui/PhotoLightbox';

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h} h ${min % 60} min`;
}

function Avatar({ etu, size = 36, onClick }: {
  etu: EtudiantConnecteItem; size?: number; onClick?: () => void;
}) {
  const initials = (etu.prenoms?.[0] || '') + (etu.nom?.[0] || '');
  const clickableClass = onClick && etu.photo_url
    ? 'cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition-shadow'
    : '';
  if (etu.photo_url) {
    return (
      <img
        src={etu.photo_url}
        alt=""
        style={{ width: size, height: size }}
        onClick={onClick}
        className={`rounded-full object-cover flex-shrink-0 bg-gray-200 dark:bg-gray-700 ${clickableClass}`}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      className="rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-semibold flex items-center justify-center flex-shrink-0"
    >
      {initials.toUpperCase() || '?'}
    </div>
  );
}

export const EtudiantsConnectes: React.FC = () => {
  const [fenetre, setFenetre] = useState(5);
  const [classeFilter, setClasseFilter] = useState('');
  const [niveauFilter, setNiveauFilter] = useState('');
  const [poleFilter, setPoleFilter] = useState('');
  const [q, setQ] = useState('');
  // Photo agrandissable (fix 2026-08-31)
  const [lightbox, setLightbox] = useState<{ url: string; label: string; hint: string } | null>(null);

  // Auto-refresh toutes les 30 secondes
  const { data, isFetching, refetch } = useGetEtudiantsConnectesQuery(
    {
      fenetre_minutes: fenetre,
      classe: classeFilter || undefined,
      niveau: niveauFilter || undefined,
      pole: poleFilter || undefined,
      q: q || undefined,
    },
    { pollingInterval: 30000 },
  );

  const items = data?.results || [];
  const total = data?.total || 0;

  const classesList = useMemo(() => data?.par_classe || [], [data]);
  const niveauxList = useMemo(() => data?.par_niveau || [], [data]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Étudiants en ligne
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Activité &lt; {fenetre} minutes — actualisation auto toutes les 30 s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg text-sm font-semibold">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {total} en ligne
          </span>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg"
          >
            {isFetching ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher (matricule, nom, prénom)..."
          className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <select
          value={niveauFilter}
          onChange={(e) => setNiveauFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous niveaux</option>
          {['L1', 'L2', 'L3', 'M1', 'M2'].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select
          value={classeFilter}
          onChange={(e) => setClasseFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 max-w-[200px]"
        >
          <option value="">Toutes classes</option>
          {classesList.map(c => (
            <option key={c.classe} value={c.classe}>{c.classe} ({c.nb})</option>
          ))}
        </select>
        <select
          value={poleFilter}
          onChange={(e) => setPoleFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous pôles</option>
          <option value="Polytechnique">Polytechnique</option>
          <option value="Commerce">Commerce</option>
          <option value="Juridique">Juridique</option>
        </select>
        <select
          value={fenetre}
          onChange={(e) => setFenetre(parseInt(e.target.value, 10))}
          title="Fenêtre de détection 'en ligne'"
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value={2}>2 min</option>
          <option value={5}>5 min</option>
          <option value={10}>10 min</option>
          <option value={15}>15 min</option>
          <option value={30}>30 min</option>
          <option value={60}>1 h</option>
        </select>
        {(q || classeFilter || niveauFilter || poleFilter) && (
          <button
            onClick={() => { setQ(''); setClasseFilter(''); setNiveauFilter(''); setPoleFilter(''); }}
            className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Liste */}
      {items.length === 0 ? (
        <div className="p-10 text-center text-gray-400 dark:text-gray-500">
          {isFetching ? (
            <div>Chargement…</div>
          ) : (
            <div>
              <div className="text-base font-medium">Aucun étudiant en ligne</div>
              <div className="text-xs mt-1">avec ces filtres et fenêtre de {fenetre} min</div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Étudiant</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Matricule</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Classe</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Option</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">Pôle</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Dernière activité</th>
                <th className="px-3 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase">Visites</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {items.map((etu) => (
                <tr key={etu.matricule} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        <Avatar
                          etu={etu} size={36}
                          onClick={etu.photo_url ? () => setLightbox({
                            url: etu.photo_url!,
                            label: etu.nom_complet,
                            hint: `${etu.matricule} · ${etu.classe || ''}`,
                          }) : undefined}
                        />
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-white dark:border-gray-800 pointer-events-none" />
                      </div>
                      <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                        {etu.nom_complet}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{etu.matricule}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{etu.classe || '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={etu.option}>
                    {etu.option || '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 dark:text-gray-400">{etu.pole || '—'}</td>
                  <td className="px-3 py-2 text-center text-[11px] text-gray-600 dark:text-gray-400">
                    il y a {timeAgo(etu.derniere_activite)}
                  </td>
                  <td className="px-3 py-2 text-center text-[11px] text-gray-600 dark:text-gray-400 font-mono">
                    {etu.nb_visites}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Lightbox photo (fix 2026-08-31) */}
      <PhotoLightbox
        url={lightbox?.url || null}
        label={lightbox?.label}
        hint={lightbox?.hint}
        onClose={() => setLightbox(null)}
      />
    </div>
  );
};
